import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';

const CartContext = createContext(null);
const STORAGE_KEY = 'sushiexpress_cart_v2';

function loadStorage() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem('sushiexpress_cart_v1');
      if (raw) localStorage.removeItem('sushiexpress_cart_v1');
    }
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Validate branchId is a positive integer
function validBranch(id) {
  return id && Number.isFinite(id) && id > 0;
}

const _initial = loadStorage();

export function CartProvider({ children }) {
  const [items, setItems] = useState(_initial?.items || []);
  const [zone, setZoneState] = useState(_initial?.zone || null);
  const [userLocation, setUserLocationState] = useState(_initial?.userLocation || null);
  const [orderType, setOrderType] = useState(_initial?.orderType || 'delivery');
  const [couponCode, setCouponCode] = useState(_initial?.couponCode || '');
  const [defaultBranchId, setDefaultBranchId] = useState(null);
  const repriceRunning = useRef(false);

  // Derived branchId: zone > default fallback, NEVER 0
  const branchId = validBranch(zone?.branch_id) ? zone.branch_id : (validBranch(defaultBranchId) ? defaultBranchId : null);

  // Fetch default branch on mount (first active branch)
  useEffect(() => {
    if (validBranch(zone?.branch_id)) return;
    api.get('/sucursales?active=true').then(r => {
      const branches = r.data.sucursales || [];
      if (branches.length > 0) setDefaultBranchId(branches[0].id);
    }).catch(() => {});
  }, [zone?.branch_id]);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, zone, userLocation, orderType, couponCode }));
  }, [items, zone, userLocation, orderType, couponCode]);

  // Cleanup old keys
  useEffect(() => {
    ['sushi_cart', 'sushi_zone', 'sushi_location', 'sushiexpress_cart_v1'].forEach(k => localStorage.removeItem(k));
  }, []);

  const setZone = (z) => setZoneState(z);
  const setUserLocation = (loc) => setUserLocationState(loc);

  // Cart migration: fix items without branch_id or with branch_id=0
  const migrateCart = useCallback((bid) => {
    if (!validBranch(bid)) return;
    setItems(prev => {
      const needsMigration = prev.some(i => !validBranch(i.branch_id));
      if (!needsMigration) return prev;
      const needsVariantSelection = [];
      const migrated = prev.map(item => {
        if (validBranch(item.branch_id)) return item;
        const updated = { ...item, branch_id: bid };
        if (item.variant_id && !item.variant_price) {
          needsVariantSelection.push(item.name);
        }
        return updated;
      });
      if (needsVariantSelection.length > 0) {
        setTimeout(() => toast.info(`Verificá las variantes de: ${needsVariantSelection.join(', ')}`), 200);
      }
      return migrated;
    });
  }, []);

  // Re-price cart (variant-aware), NEVER calls with branchId=0
  const repriceCart = useCallback(async (bid) => {
    if (!validBranch(bid) || repriceRunning.current) return;
    repriceRunning.current = true;
    try {
      const { data } = await api.get(`/productos?branch_id=${bid}`);
      const productMap = {};
      data.productos.forEach(p => {
        const price = p.current_price ? parseFloat(p.current_price) : (p.branch_price ? parseFloat(p.branch_price) : null);
        productMap[p.id] = {
          price,
          available: !!price,
          variant_required: !!p.variant_required,
          variant_count: parseInt(p.variant_count) || 0,
          promo: p.promo_active ? { label: p.promo_label, regular: parseFloat(p.branch_price) } : null,
        };
      });

      // Fetch variant prices for products that have variants in the cart
      const variantProductIds = [...new Set(items.filter(i => i.variant_id).map(i => i.product_id))];
      const variantPriceMap = {};
      for (const pid of variantProductIds) {
        try {
          const { data: vd } = await api.get(`/productos/${pid}/variantes/sucursal/${bid}`);
          (vd.variantes || []).forEach(v => {
            variantPriceMap[v.id] = {
              price: parseFloat(v.effective_price),
              active: v.active,
              name: v.name,
            };
          });
        } catch { /* ignore */ }
      }

      setItems(prev => {
        if (prev.length === 0) return prev;
        const removed = [];
        const updated = prev.filter(item => {
          if (!productMap[item.product_id] || !productMap[item.product_id].available) {
            removed.push({ name: item.name, reason: 'no disponible en esta sucursal' });
            return false;
          }
          // Check variant availability
          if (item.variant_id) {
            const vInfo = variantPriceMap[item.variant_id];
            if (vInfo && !vInfo.active) {
              removed.push({ name: `${item.name} (${item.variant_name || 'variante'})`, reason: 'variante no disponible' });
              return false;
            }
          }
          // Product requires variant but item has none
          const pm = productMap[item.product_id];
          if (pm.variant_required && pm.variant_count > 0 && !item.variant_id) {
            removed.push({ name: item.name, reason: 'requiere seleccionar variante' });
            return false;
          }
          return true;
        }).map(item => {
          // Variant items: use branch-specific variant pricing
          if (item.variant_id && variantPriceMap[item.variant_id]) {
            return { ...item, unit_price: variantPriceMap[item.variant_id].price, variant_price: variantPriceMap[item.variant_id].price, branch_id: bid, promo: null };
          }
          // Non-variant items: use product pricing
          const pm = productMap[item.product_id];
          return { ...item, unit_price: pm.price, branch_id: bid, promo: pm.promo };
        });
        if (removed.length > 0) {
          const msg = removed.map(r => `${r.name}: ${r.reason}`).join('\n');
          setTimeout(() => toast.warning('Items eliminados del carrito', { description: msg, duration: 5000 }), 100);
        }
        return updated;
      });
    } catch { /* keep existing prices */ }
    finally { repriceRunning.current = false; }
  }, [items]);

  // Validate stored branch on mount
  useEffect(() => {
    if (!_initial?.zone?.branch_id || (_initial?.items || []).length === 0) return;
    api.get(`/sucursales/${_initial.zone.branch_id}/status`).catch(err => {
      if (err.response?.status === 404) {
        setZoneState(null);
        setUserLocationState(null);
        setItems([]);
        setOrderType('delivery');
        setCouponCode('');
        toast.warning('Tu sucursal seleccionada ya no está disponible. Elegí una nueva ubicación.');
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reprice and migrate when branchId changes
  useEffect(() => {
    if (validBranch(branchId)) {
      migrateCart(branchId);
      repriceCart(branchId);
    }
  }, [branchId, repriceCart, migrateCart]);

  useEffect(() => {
    if (validBranch(branchId)) {
      api.put('/push/last-branch', { branch_id: branchId }).catch(() => {});
    }
  }, [branchId]);

  const addItem = (product, quantity, addons = [], effectivePrice, variant = null) => {
    let wasUpdate = false;
    let newQty = quantity;
    setItems(prev => {
      const addonsHash = addons.map(a => a.id).sort().join(',');
      const key = variant
        ? `${product.id}-v${variant.id}-${addonsHash}`
        : `${product.id}-novar-${addonsHash}`;
      const existing = prev.findIndex(i => i.key === key);
      if (existing >= 0) {
        wasUpdate = true;
        newQty = prev[existing].quantity + quantity;
        const updated = [...prev];
        updated[existing] = { ...updated[existing], quantity: newQty, unit_price: effectivePrice };
        return updated;
      }
      return [...prev, {
        key,
        product_id: product.id,
        name: product.name,
        image_url: product.image_url,
        unit_price: effectivePrice || 0,
        quantity,
        addons: addons.map(a => ({ id: a.id, name: a.name, extra_cost: parseFloat(a.extra_cost) })),
        variant_id: variant?.id || null,
        variant_name: variant?.name || null,
        variant_price: variant ? effectivePrice : null,
        branch_id: branchId || null,
      }];
    });
    return { wasUpdate, quantity: wasUpdate ? newQty : quantity };
  };

  const updateQuantity = (key, quantity) => {
    if (quantity <= 0) return removeItem(key);
    setItems(prev => prev.map(i => i.key === key ? { ...i, quantity } : i));
  };

  const removeItem = (key) => {
    setItems(prev => prev.filter(i => i.key !== key));
  };

  const clearCart = () => {
    setItems([]);
    setOrderType('delivery');
    setCouponCode('');
  };

  const itemCount = items.reduce((s, i) => s + i.quantity, 0);

  const subtotal = items.reduce((s, i) => {
    const addonsCost = i.addons.reduce((a, ad) => a + ad.extra_cost, 0);
    return s + (i.unit_price + addonsCost) * i.quantity;
  }, 0);

  const deliveryFee = zone ? parseFloat(zone.delivery_price) : 0;
  const total = subtotal + deliveryFee;

  return (
    <CartContext.Provider value={{
      items, addItem, updateQuantity, removeItem, clearCart,
      itemCount, subtotal, deliveryFee, total,
      zone, setZone, branchId,
      userLocation, setUserLocation,
      orderType, setOrderType,
      couponCode, setCouponCode,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
